import {
  COLLECTION_CURRENCIES,
  DEFAULT_CURRENCY,
  deriveRequirementSet,
  type FlowPageType,
  getCurrencyLabel,
  renderedRiskSections,
  resolveRiskInputScopes,
} from "@repo/shared";
import type { CreateFlowFromTemplateRequest, RiskModelScopes } from "@repo/shared/types";
import { ChevronDown, ChevronRight } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogCancel,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SearchInput } from "@/components/ui/search-input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { post } from "@/lib/api/_client";
import { useUserLanguage } from "@/lib/i18n/context";
import { useT } from "@/lib/i18n/use-t";
import { cn } from "@/lib/utils";
import { T } from "./flow-template-dialog.i18n";
import {
  FLOW_TEMPLATES,
  type FlowTemplate,
  type FlowTemplateCategory,
  TEMPLATE_CATEGORIES,
} from "./templates";

// Get localized language name using Intl.DisplayNames
function getLocalizedLanguageName(langCode: string, userLanguage: string): string {
  try {
    // Convert locale codes: en_GB -> en-GB, nb_NO -> nb-NO
    const displayLocale = userLanguage.replace("_", "-");
    const targetLocale = langCode.replace("_", "-");
    const displayNames = new Intl.DisplayNames([displayLocale], { type: "language" });
    return displayNames.of(targetLocale) || langCode;
  } catch {
    return langCode;
  }
}

type FlowTemplateDialogProps = {
  open: boolean;
  onClose: () => void;
  riskModels: Array<{ id: string; name: string } & Partial<RiskModelScopes>>;
  portfolioCollections: Array<{
    id: string;
    name: string;
    riskModelId: string;
    requiresAssetAllocation: boolean;
    requiresTimeseries: boolean;
    currency: string;
  }>;
  financialSituationModels: Array<{ id: string; name: string }>;
  supportedLanguages: string[];
  defaultLanguage: string;
};

export function FlowTemplateDialog({
  open,
  onClose,
  riskModels,
  portfolioCollections,
  financialSituationModels,
  supportedLanguages,
  defaultLanguage,
}: FlowTemplateDialogProps) {
  const navigate = useNavigate();
  const { userLanguage } = useUserLanguage();
  const t = useT(T);
  const [step, setStep] = useState<"select" | "configure">("select");
  const [selectedTemplate, setSelectedTemplate] = useState<FlowTemplate | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(["advisory-single"]),
  );
  const [name, setName] = useState("");
  const [language, setLanguage] = useState(
    supportedLanguages.includes(userLanguage) ? userLanguage : defaultLanguage,
  );
  const [riskModelId, setRiskModelId] = useState<string | null>(null);
  const [portfolioCollectionId, setPortfolioCollectionId] = useState<string | null>(null);
  const [financialSituationModelId, setFinancialSituationModelId] = useState<string | null>(null);
  // QL-372 — the flow currency. Collections are filtered to it, so the picker
  // locks once a collection is chosen.
  const [currency, setCurrency] = useState(DEFAULT_CURRENCY);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open) {
      setStep("select");
      setSelectedTemplate(null);
      setSearchQuery("");
      setExpandedCategories(new Set(["advisory-single"]));
      setName("");
      setLanguage(supportedLanguages.includes(userLanguage) ? userLanguage : defaultLanguage);
      setRiskModelId(null);
      setPortfolioCollectionId(null);
      setFinancialSituationModelId(null);
      setCurrency(DEFAULT_CURRENCY);
      setError(null);
    }
  }, [open, userLanguage, supportedLanguages, defaultLanguage]);

  // Update name when template is selected
  useEffect(() => {
    if (selectedTemplate) {
      setName(selectedTemplate.name);
    }
  }, [selectedTemplate]);

  // Reset selected template when language changes
  useEffect(() => {
    setSelectedTemplate(null);
  }, [language]);

  const handleNext = () => {
    if (selectedTemplate) {
      setStep("configure");
    }
  };

  const handleBack = () => {
    setStep("select");
    setError(null);
  };

  // Filter portfolio collections by the selected risk model and the chosen
  // currency (QL-372: a flow only links a collection in its own currency).
  const availablePortfolioCollections = portfolioCollections.filter(
    (pc) => pc.riskModelId === riskModelId && pc.currency === currency,
  );
  const selectedCollection = availablePortfolioCollections.find(
    (pc) => pc.id === portfolioCollectionId,
  );

  // Reset portfolio collection if risk model changes
  useEffect(() => {
    if (
      portfolioCollectionId &&
      !availablePortfolioCollections.find((pc) => pc.id === portfolioCollectionId)
    ) {
      setPortfolioCollectionId(null);
    }
  }, [riskModelId, portfolioCollectionId, availablePortfolioCollections]);

  // Requirements derived from the template's actual components (single source of
  // truth in @repo/shared) — drives the asterisks, the Requirements panel, and
  // the Create button's disabled state. Never read hand-maintained template flags.
  const requiredFlags = useMemo(() => {
    const componentTypes: string[] = [];
    for (const page of selectedTemplate?.pages ?? []) {
      for (const layout of page.layouts ?? []) {
        for (const comp of layout.components ?? []) componentTypes.push(comp.type);
      }
      for (const comp of page.components ?? []) componentTypes.push(comp.type);
    }
    return deriveRequirementSet(componentTypes).flags;
  }, [selectedTemplate]);

  // QL-212 — the page types this template places `risk_assessment` on. A template
  // is written against the default (per-goal) scopes, so pairing it with an
  // all-global risk model leaves every one of those placements rendering nothing.
  // The server copies a template verbatim rather than refusing the combination,
  // so the warning below is where the designer finds out.
  const riskPlacementPageTypes = useMemo(() => {
    const pageTypes: FlowPageType[] = [];
    for (const page of selectedTemplate?.pages ?? []) {
      const components = [
        ...(page.layouts ?? []).flatMap((layout) => layout.components ?? []),
        ...(page.components ?? []),
      ];
      if (components.some((comp) => comp.type === "risk_assessment")) {
        pageTypes.push(page.pageType);
      }
    }
    return pageTypes;
  }, [selectedTemplate]);

  // True only when the template DOES place the component and NONE of those
  // placements would render a section under the chosen model's scopes. Advice,
  // never a gate: the flow is still creatable and the component can be moved.
  const riskScopeMismatch = useMemo(() => {
    if (riskPlacementPageTypes.length === 0) return false;
    const model = riskModels.find((m) => m.id === riskModelId);
    if (!model) return false;
    const scopes = resolveRiskInputScopes(model);
    return riskPlacementPageTypes.every(
      (pageType) => renderedRiskSections(scopes, pageType).size === 0,
    );
  }, [riskPlacementPageTypes, riskModels, riskModelId]);

  // Setting keys the template requires but the given collection doesn't satisfy.
  // Drives the disabled state and inline reason in the collection picker.
  const missingSettingsFor = (c: {
    requiresAssetAllocation: boolean;
    requiresTimeseries: boolean;
  }) => {
    const missing: Array<"requiresAssetAllocation" | "requiresTimeseries"> = [];
    if (requiredFlags.requiresAssetAllocation && !c.requiresAssetAllocation)
      missing.push("requiresAssetAllocation");
    if (requiredFlags.requiresTimeseries && !c.requiresTimeseries)
      missing.push("requiresTimeseries");
    return missing;
  };

  const allRequirementsSatisfied =
    name.trim().length > 0 &&
    (!requiredFlags.requiresRiskModel || !!riskModelId) &&
    (!requiredFlags.requiresPortfolioCollection ||
      (!!portfolioCollectionId &&
        availablePortfolioCollections.some(
          (pc) => pc.id === portfolioCollectionId && missingSettingsFor(pc).length === 0,
        ))) &&
    (!requiredFlags.requiresFinancialSituationModel || !!financialSituationModelId);

  // Group templates by category, filtered by language
  const templatesByCategory = useMemo(() => {
    const filtered = FLOW_TEMPLATES.filter((template) => {
      // Filter by language first
      if (template.language !== language) return false;

      // Then filter by search query
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return (
        template.name.toLowerCase().includes(query) ||
        template.description.toLowerCase().includes(query) ||
        template.badges.some((badge) => badge.toLowerCase().includes(query))
      );
    });

    const grouped: Record<FlowTemplateCategory, FlowTemplate[]> = {
      execution: [],
      "advisory-single": [],
      "advisory-multi": [],
      "self-service": [],
      specialized: [],
    };

    filtered.forEach((template) => {
      grouped[template.category].push(template);
    });

    return grouped;
  }, [searchQuery, language]);

  // Get sorted categories
  const sortedCategories = useMemo(() => {
    return (Object.keys(TEMPLATE_CATEGORIES) as FlowTemplateCategory[]).sort(
      (a, b) => TEMPLATE_CATEGORIES[a].order - TEMPLATE_CATEGORIES[b].order,
    );
  }, []);

  // Auto-expand all categories when searching
  useEffect(() => {
    if (searchQuery.trim()) {
      setExpandedCategories(
        new Set(sortedCategories.filter((cat) => templatesByCategory[cat].length > 0)),
      );
    }
  }, [searchQuery, sortedCategories, templatesByCategory]);

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories((prev) => {
      if (prev.has(categoryId)) {
        // Closing this category
        return new Set();
      }
      // Opening this category - also select first template
      const firstInCategory = templatesByCategory[categoryId as FlowTemplateCategory]?.[0];
      if (firstInCategory) {
        setSelectedTemplate(firstInCategory);
      }
      return new Set([categoryId]);
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTemplate) return;

    if (!name.trim()) {
      setError(t.flowNameRequired);
      return;
    }

    setError(null);
    setLoading(true);

    try {
      const data = await post<{ id: string }>("/internal/flows/from-template", {
        name: name.trim(),
        language,
        riskModelId: riskModelId || undefined,
        portfolioCollectionId: portfolioCollectionId || undefined,
        financialSituationModelId: financialSituationModelId || undefined,
        currency,
        multiGoal: selectedTemplate.multiGoal,
        multiPortfolio: selectedTemplate.multiPortfolio ?? false,
        allowPublicStart: selectedTemplate.allowPublicStart,
        allowPublicPdfGen: selectedTemplate.allowPublicPdfGen,
        allowPublicSigning: selectedTemplate.allowPublicSigning,
        pages: selectedTemplate.pages,
      } satisfies CreateFlowFromTemplateRequest);

      // Navigate to the new flow editor
      if (data.id) {
        navigate(`/settings/flows/${data.id}`);
      }

      onClose();
    } catch (_err) {
      setError(t.failedToCreateFromTemplate);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-4xl h-[600px] flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {step === "select"
              ? t.createFromTemplate
              : `${t.configureTemplate}: ${selectedTemplate?.name}`}
          </DialogTitle>
          <DialogDescription>
            {step === "select" ? t.templateDialogDescription : t.configureTemplateDescription}
          </DialogDescription>
        </DialogHeader>

        {step === "select" ? (
          <>
            {/* Language selector at the top */}
            <div className="flex items-center gap-3 py-2">
              <Label htmlFor="language-select" className="text-sm whitespace-nowrap">
                {t.language}:
              </Label>
              <Select value={language} onValueChange={setLanguage}>
                <SelectTrigger id="language-select" className="w-48">
                  <SelectValue placeholder={t.selectLanguage} />
                </SelectTrigger>
                <SelectContent>
                  {supportedLanguages.map((lang) => (
                    <SelectItem key={lang} value={lang}>
                      {getLocalizedLanguageName(lang, userLanguage)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-[240px_1fr] gap-4 flex-1 overflow-hidden">
              {/* Left: Categories and template list */}
              <div className="border rounded-lg overflow-hidden flex flex-col">
                {/* Search input */}
                <div className="p-2 border-b">
                  <SearchInput
                    data-dialog-guard-ignore
                    placeholder={t.searchTemplates}
                    value={searchQuery}
                    onValueChange={setSearchQuery}
                    inputClassName="h-8 text-sm"
                  />
                </div>

                {/* Template list grouped by collapsible categories */}
                <div className="flex-1 overflow-y-auto">
                  {sortedCategories.every((cat) => templatesByCategory[cat].length === 0) ? (
                    <div className="p-4 text-sm text-muted-foreground text-center">
                      {t.noTemplatesFound}
                    </div>
                  ) : (
                    sortedCategories.map((category) => {
                      const templates = templatesByCategory[category];
                      if (templates.length === 0) return null;

                      const isSearching = searchQuery.trim().length > 0;
                      const isExpanded = isSearching || expandedCategories.has(category);
                      const hasSelectedTemplate = templates.some(
                        (template) => template.key === selectedTemplate?.key,
                      );

                      return (
                        <div key={category}>
                          {/* Category header */}
                          <button
                            type="button"
                            onClick={() => !isSearching && toggleCategory(category)}
                            className={cn(
                              "w-full flex items-center justify-between px-3 py-2 bg-muted/50 border-b text-sm font-medium transition-colors",
                              isSearching ? "cursor-default" : "hover:bg-muted",
                              hasSelectedTemplate && !isExpanded ? "text-primary" : "",
                            )}
                          >
                            <span className="flex items-center gap-2">
                              {!isSearching &&
                                (isExpanded ? (
                                  <ChevronDown className="h-4 w-4" />
                                ) : (
                                  <ChevronRight className="h-4 w-4" />
                                ))}
                              <span className="truncate">{t.categories[category]}</span>
                            </span>
                            <span className="text-xs text-muted-foreground">
                              ({templates.length})
                            </span>
                          </button>

                          {/* Templates in category */}
                          {isExpanded &&
                            templates.map((template) => (
                              <button
                                key={template.key}
                                type="button"
                                onClick={() => setSelectedTemplate(template)}
                                className={cn(
                                  "w-full text-left pl-9 pr-3 py-2 border-b last:border-b-0 transition-colors",
                                  selectedTemplate?.key === template.key
                                    ? "bg-primary text-primary-foreground"
                                    : "hover:bg-muted",
                                )}
                              >
                                <div className="font-medium text-sm truncate">{template.name}</div>
                                <div
                                  className={cn(
                                    "text-xs truncate",
                                    selectedTemplate?.key === template.key
                                      ? "text-primary-foreground/70"
                                      : "text-muted-foreground",
                                  )}
                                >
                                  {template.pages.length} {t.pages.toLowerCase()}
                                </div>
                              </button>
                            ))}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              {/* Right: Template details */}
              <div className="p-4 border rounded-lg bg-muted/30 overflow-y-auto">
                {selectedTemplate ? (
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">{selectedTemplate.name}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {selectedTemplate.description}
                    </p>

                    {/* Badges */}
                    <div className="flex flex-wrap gap-1">
                      {selectedTemplate.badges.map((badge) => (
                        <Badge key={badge} variant="outline" className="text-xs">
                          {badge}
                        </Badge>
                      ))}
                      {selectedTemplate.multiGoal && (
                        <Badge variant="secondary" className="text-xs">
                          {t.multiGoal}
                        </Badge>
                      )}
                      {selectedTemplate.allowPublicStart && (
                        <Badge variant="secondary" className="text-xs">
                          {t.publicStart}
                        </Badge>
                      )}
                    </div>

                    {/* Requirements (derived from the template's components) */}
                    {(requiredFlags.requiresRiskModel ||
                      requiredFlags.requiresPortfolioCollection ||
                      requiredFlags.requiresFinancialSituationModel) && (
                      <div className="pt-2 space-y-1">
                        <p className="text-xs font-medium text-muted-foreground">
                          {t.requirements}
                        </p>
                        <ul className="text-xs text-muted-foreground list-disc list-inside space-y-0.5">
                          {requiredFlags.requiresRiskModel && <li>{t.riskModelRequired}</li>}
                          {requiredFlags.requiresPortfolioCollection && (
                            <li>{t.portfolioCollectionRequired}</li>
                          )}
                          {requiredFlags.requiresFinancialSituationModel && (
                            <li>{t.financialSituationModelRequired}</li>
                          )}
                        </ul>
                      </div>
                    )}

                    {/* Page summary */}
                    <div className="pt-2 border-t">
                      <p className="text-xs font-medium text-muted-foreground mb-2">
                        {t.pages} ({selectedTemplate.pages.length}):
                      </p>
                      <div className="space-y-1">
                        {selectedTemplate.pages.map((page, idx) => (
                          <div
                            key={`${page.name}-${page.visibility}`}
                            className="flex items-center gap-2 text-xs text-muted-foreground"
                          >
                            <span className="w-4 text-center">{idx + 1}.</span>
                            <span className="flex-1 truncate">{page.name}</span>
                            <Badge
                              variant={page.visibility === "public" ? "default" : "secondary"}
                              className="text-[10px] px-1 py-0"
                            >
                              {page.visibility === "public" ? t.public : t.internal}
                            </Badge>
                            <Badge variant="outline" className="text-[10px] px-1 py-0">
                              {page.pageType === "goal"
                                ? t.goal
                                : page.pageType === "portfolio"
                                  ? t.portfolio
                                  : t.global}
                            </Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground">{t.selectTemplatePrompt}</p>
                )}
              </div>
            </div>

            <DialogFooter>
              <DialogCancel>{t.cancel}</DialogCancel>
              <Button onClick={handleNext} disabled={!selectedTemplate}>
                {t.next}
              </Button>
            </DialogFooter>
          </>
        ) : (
          // Step 2: Configure
          <form onSubmit={handleSubmit} className="flex-1 flex flex-col py-4">
            <div className="flex-1 space-y-6">
              {error && (
                <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <Label>{t.language}</Label>
                <p className="text-sm text-muted-foreground">
                  {getLocalizedLanguageName(language, userLanguage)}
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="flow-name">
                  {t.flowName} <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="flow-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t.clientOnboardingPlaceholder}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="risk-model">
                  {t.riskModel}
                  {requiredFlags.requiresRiskModel && (
                    <span className="text-destructive ml-1">*</span>
                  )}
                </Label>
                <Select
                  value={riskModelId || "none"}
                  onValueChange={(value) => setRiskModelId(value === "none" ? null : value)}
                >
                  <SelectTrigger id="risk-model">
                    <SelectValue placeholder={t.selectRiskModel} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">
                      <span className="text-muted-foreground">{t.noRiskModel}</span>
                    </SelectItem>
                    {riskModels.map((model) => (
                      <SelectItem key={model.id} value={model.id}>
                        {model.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {requiredFlags.requiresRiskModel && !riskModelId && (
                  <p className="text-xs text-warning">{t.requiredForTemplate}</p>
                )}
                {riskScopeMismatch && <p className="text-xs text-warning">{t.riskScopeMismatch}</p>}
                <p className="text-xs text-muted-foreground">{t.requiredForRiskAssessment}</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="flow-currency">{t.currency}</Label>
                {selectedCollection ? (
                  <>
                    <p className="text-sm text-muted-foreground">
                      {getCurrencyLabel(selectedCollection.currency, userLanguage)}
                    </p>
                    <p className="text-xs text-muted-foreground">{t.currencyFromCollection}</p>
                  </>
                ) : (
                  <>
                    <Select value={currency} onValueChange={setCurrency}>
                      <SelectTrigger id="flow-currency">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {COLLECTION_CURRENCIES.map((c) => (
                          <SelectItem key={c.code} value={c.code}>
                            {getCurrencyLabel(c.code, userLanguage)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">{t.currencyHint}</p>
                  </>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="portfolio-collection">
                  {t.portfolioCollection}
                  {requiredFlags.requiresPortfolioCollection && (
                    <span className="text-destructive ml-1">*</span>
                  )}
                </Label>
                <Select
                  value={portfolioCollectionId || "none"}
                  onValueChange={(value) =>
                    setPortfolioCollectionId(value === "none" ? null : value)
                  }
                  disabled={!riskModelId}
                >
                  <SelectTrigger id="portfolio-collection">
                    <SelectValue placeholder={t.selectPortfolioCollection} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">
                      <span className="text-muted-foreground">{t.noCollection}</span>
                    </SelectItem>
                    {availablePortfolioCollections.map((collection) => {
                      const missing = missingSettingsFor(collection);
                      const reason = missing
                        .map((k) =>
                          k === "requiresTimeseries"
                            ? t.missingTimeseries
                            : t.missingAssetAllocation,
                        )
                        .join(", ");
                      return (
                        <SelectItem
                          key={collection.id}
                          value={collection.id}
                          disabled={missing.length > 0}
                        >
                          <span>{collection.name}</span>
                          {missing.length > 0 && (
                            <span className="text-muted-foreground">
                              {" "}
                              — {t.missingPrefix} {reason}
                            </span>
                          )}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
                {!riskModelId ? (
                  <p className="text-xs text-muted-foreground">{t.selectRiskModelFirst}</p>
                ) : requiredFlags.requiresPortfolioCollection && !portfolioCollectionId ? (
                  <p className="text-xs text-warning">{t.requiredForTemplate}</p>
                ) : (
                  <p className="text-xs text-muted-foreground">{t.requiredForPortfolioFeatures}</p>
                )}
                {riskModelId && (
                  <p className="text-xs text-muted-foreground">
                    {t.collectionsInCurrency(currency)}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="financial-situation-model">
                  {t.financialSituationModel}
                  {requiredFlags.requiresFinancialSituationModel && (
                    <span className="text-destructive ml-1">*</span>
                  )}
                </Label>
                <Select
                  value={financialSituationModelId || "none"}
                  onValueChange={(value) =>
                    setFinancialSituationModelId(value === "none" ? null : value)
                  }
                >
                  <SelectTrigger id="financial-situation-model">
                    <SelectValue placeholder={t.selectFinancialSituationModel} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">
                      <span className="text-muted-foreground">{t.noFinancialSituationModel}</span>
                    </SelectItem>
                    {financialSituationModels.map((model) => (
                      <SelectItem key={model.id} value={model.id}>
                        {model.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {requiredFlags.requiresFinancialSituationModel && !financialSituationModelId && (
                  <p className="text-xs text-warning">{t.requiredForTemplate}</p>
                )}
                <p className="text-xs text-muted-foreground">{t.requiredForFinancialSituation}</p>
              </div>
            </div>

            <DialogFooter className="pt-4">
              <Button type="button" variant="outline" onClick={handleBack} disabled={loading}>
                {t.back}
              </Button>
              <Button type="submit" loading={loading} disabled={!allRequirementsSatisfied}>
                {t.create}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
